import Phaser from 'phaser'
import { Player } from '../entities/Player'
import { Enemy, type StageEnemy } from '../entities/Enemy'
import { Flyer } from '../entities/Flyer'
import { Turret } from '../entities/Turret'
import { Charger } from '../entities/Charger'
import { Spitter } from '../entities/Spitter'
import { Boss } from '../entities/Boss'
import { Bullet } from '../objects/Bullet'
import { drawVignette } from '../ui'
import { sfx, startMusic, stopMusic } from '../audio'
import { askAI } from '../ai'
import { getCompanion, type CompanionDef } from '../companions'
import { STAGES, DEFAULT_STAGE, type StageDef } from '../stages'
import { WEAPONS, BOSS_WEAPON, BOSS_WEAKNESS, type WeaponId } from '../weapons'
import { getCapsule, COMBAT, type CombatPower } from '../capsules'
import { touchState, isTouchUI, consumeTouchEdges } from '../touch'
import { getDifficulty, type Difficulty } from '../difficulty'

const WORLD_H = 320
// Actualisées en create() depuis level-entities.json (niveau généré).
let WORLD_W = 800

/** Contenu de level-entities.json (voir scripts/generate-level.mjs). */
interface LevelEntities {
  worldW: number
  spawnX: number
  spawnY: number
  bossX: number
  bossWarnX: number
  enemies: Array<{ kind: string; x: number; y: number }>
  checkpoints: Array<{ x: number; y: number }>
  orbs: Array<{ x: number; y: number }>
  capsules?: Array<{ x: number; y: number; type: string }>
}

export class GameScene extends Phaser.Scene {
  player!: Player
  private ground!: Phaser.Tilemaps.TilemapLayer
  private platforms!: Phaser.Tilemaps.TilemapLayer
  private enemies!: Phaser.Physics.Arcade.Group
  private bullets!: Phaser.Physics.Arcade.Group
  private orbs!: Phaser.Physics.Arcade.Group
  private enemyBullets!: Phaser.Physics.Arcade.Group
  private ents!: LevelEntities
  private checkpoints: Phaser.GameObjects.Image[] = []
  private respawning = false
  private bossIntroDone = false
  private bossTauntBusy = false
  private bossTauntTimer = 6000
  private bossSpeech?: Phaser.GameObjects.Container
  private lastTaunt = ''
  private compData!: CompanionDef
  private companion!: Phaser.GameObjects.Image
  private companionGlow!: Phaser.GameObjects.Image
  private compBubble?: Phaser.GameObjects.Container
  private compBusy = false
  private compCooldown = 5000
  private compSaid = new Set<string>()
  private compT = 0
  // Timers des pouvoirs de compagnon (capsules).
  private compShootT = 0
  private compShieldT = 0
  private compHealT = 0
  private compSignature: CombatPower = 'tir'
  private compIcons: Phaser.GameObjects.Image[] = []
  private readonly fallbackTaunts: Record<string, string[]> = {
    intro: ['Ton existence prend fin ici, gardien.', 'Néon City m’appartient déjà.'],
    combat: ['Ta résistance est une erreur de programmation.', 'Je calcule déjà ta défaite.', "Chaque tir t'affaiblit. Chaque seconde me rend plus fort."],
    enraged: ['ASSEZ ! Je vais te rayer de mes registres !', 'Mes circuits brûlent... ta fin approche !'],
  }
  private boss?: Boss
  private bossBar!: Phaser.GameObjects.Graphics
  private bossName!: Phaser.GameObjects.Text
  private bossActive = false
  private lastBlockMsg = 0
  private hpBar!: Phaser.GameObjects.Graphics
  private powerText!: Phaser.GameObjects.Text
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys
  private shootKey!: Phaser.Input.Keyboard.Key
  private muteKey!: Phaser.Input.Keyboard.Key
  private pauseKey!: Phaser.Input.Keyboard.Key
  private escKey!: Phaser.Input.Keyboard.Key
  private muteToast?: Phaser.GameObjects.Text
  private weBar!: Phaser.GameObjects.Graphics
  private bgFar!: Phaser.GameObjects.TileSprite
  private bgMid!: Phaser.GameObjects.TileSprite
  private ambientEvt?: Phaser.Time.TimerEvent
  private enemyCount = 0
  private stage: StageDef = DEFAULT_STAGE
  private diff!: Difficulty
  // Previous-frame state of the touch buttons (edge detection for jump/shoot).
  private tPrevJump = false
  private tPrevShoot = false

  constructor() {
    super({ key: 'GameScene' })
  }

  init(data: { stage?: string; fresh?: boolean }) {
    this.stage = STAGES.find(s => s.id === data.stage) ?? DEFAULT_STAGE
    // A fresh entry from the stage select resets lives and checkpoint; a death-restart keeps them.
    if (data.fresh) {
      this.registry.set('lives', 3)
      this.registry.set('cpX', undefined)
      this.registry.set('cpY', undefined)
      this.registry.set('briefed', false)
    }
    if (this.registry.get('lives') === undefined) {
      this.registry.set('lives', 3)
    }
    // Appuis tactiles hérités de l'écran précédent : ignorés au spawn.
    this.tPrevJump = touchState.jump
    this.tPrevShoot = touchState.shoot
  }

  preload() {
    // Assets rechargés par niveau (même clé, URL différente selon le stage) :
    // quand on enchaîne deux niveaux dans la même session, Phaser garde la
    // ressource du niveau précédent en cache (clé déjà présente => pas de reload),
    // ce qui affiche les mauvais sprites / la mauvaise géométrie.
    // On purge ces assets pour forcer leur rechargement avec ceux du stage courant.
    for (const k of ['enemy', 'boss', 'flyer', 'turret', 'charger', 'spitter']) {
      if (this.textures.exists(k)) this.textures.remove(k)
    }
    if (this.cache.json.exists('level-entities')) this.cache.json.remove('level-entities')
    // Tilemap Tiled chargée sous la clé 'level' (cache 'tilemap' de Phaser).
    const c = this.cache as unknown as { tilemap?: { exists(k: string): boolean; remove(k: string): void } }
    if (c.tilemap?.exists?.('level')) c.tilemap.remove('level')

    const comp = getCompanion(this.registry.get('companion'))
    this.load.image(comp.texture, `assets/${comp.texture}.png`)
    // Textures de tir / bouclier propres à chaque compagnon + flash de muzzle.
    this.load.image('comp-orion-shot', 'assets/comp-orion-shot.png')
    this.load.image('comp-nova-missile', 'assets/comp-nova-missile.png')
    this.load.image('comp-bolt-field', 'assets/comp-bolt-field.png')
    this.load.image('comp-muzzle', 'assets/comp-muzzle.png')
    // Icônes de pouvoir du HUD (signature + boosts).
    this.load.image('icon-tir', 'assets/icon-tir.png')
    this.load.image('icon-bouclier', 'assets/icon-bouclier.png')
    this.load.image('icon-explosion', 'assets/icon-explosion.png')
    this.load.image('icon-soin', 'assets/icon-soin.png')
    this.load.image('icon-rapide', 'assets/icon-rapide.png')
    this.load.image('icon-puissance', 'assets/icon-puissance.png')
    this.load.image('rain', 'assets/rain.png')
    this.load.image('tileset', 'assets/tileset.png')
    this.load.tilemapTiledJSON('level', `assets/level-${this.stage.id}.json`)
    this.load.spritesheet('player', 'assets/player.png', { frameWidth: 22, frameHeight: 30 })
    this.load.spritesheet('enemy', `assets/enemy-${this.stage.id}.png`, { frameWidth: 22, frameHeight: 22 })
    this.load.spritesheet('boss', `assets/boss-${this.stage.id}.png`, { frameWidth: 44, frameHeight: 44 })
    this.load.image('bullet', 'assets/bullet.png')
    this.load.image('bullet-mid', 'assets/bullet-mid.png')
    this.load.image('bullet-big', 'assets/bullet-big.png')
    this.load.image('orb', 'assets/orb.png')
    this.load.spritesheet('flyer', `assets/flyer-${this.stage.id}.png`, { frameWidth: 20, frameHeight: 14 })
    this.load.image('turret', `assets/turret-${this.stage.id}.png`)
    this.load.spritesheet('charger', `assets/charger-${this.stage.id}.png`, { frameWidth: 20, frameHeight: 16 })
    this.load.spritesheet('spitter', `assets/spitter-${this.stage.id}.png`, { frameWidth: 18, frameHeight: 16 })
    this.load.image('checkpoint', 'assets/checkpoint.png')
    this.load.image('capsule', 'assets/capsule.png')
    this.load.image(`bg-far-${this.stage.id}`, `assets/bg-far-${this.stage.id}.png`)
    this.load.image(`bg-mid-${this.stage.id}`, `assets/bg-mid-${this.stage.id}.png`)
    this.load.image('haze', 'assets/haze.png')
    this.load.json('level-entities', `assets/entities-${this.stage.id}.json`)
  }

  create() {
    this.createAnimations()
    this.respawning = false

    // Niveau généré : taille du monde + entités (ennemis, checkpoints, orbes).
    this.ents = this.cache.json.get('level-entities') as LevelEntities
    WORLD_W = this.ents.worldW
    this.diff = getDifficulty(this.stage.id)

    // Clamp du delta physique à ~33 ms (plancher 30 fps) : au-delà, le jeu
    // ralentit au lieu de laisser les corps traverser les tuiles (tunneling).
    const world = this.physics.world
    if (!(world as unknown as { _deltaClamped?: boolean })._deltaClamped) {
      ;(world as unknown as { _deltaClamped?: boolean })._deltaClamped = true
      const worldUpdate = world.update.bind(world)
      world.update = (time: number, delta: number) => worldUpdate(time, Math.min(delta, 33.4))
    }

    this.cameras.main.setBounds(0, 0, WORLD_W, WORLD_H)
    // Fond du monde étendu : le joueur peut tomber dans les trous (puis mourir),
    // la caméra reste clampée à WORLD_H → il tombe hors du champ visuel.
    this.physics.world.setBounds(0, 0, WORLD_W, WORLD_H + 120)

    this.createBackground()
    this.createTilemap()
    this.setupAmbient()
    this.setupAmbientContinuous()

    this.bullets = this.physics.add.group({
      classType: Bullet,
      defaultKey: 'bullet',
      maxSize: 24,
      runChildUpdate: true,
      allowGravity: false,
      immovable: true,
    })

    this.orbs = this.physics.add.group({
      allowGravity: false,
      immovable: true,
    })

    this.enemyBullets = this.physics.add.group({
      allowGravity: false,
      immovable: true,
      maxSize: 30,
    })

    this.enemies = this.physics.add.group({
      allowGravity: true,
      collideWorldBounds: true,
    })

    const spawnX = ((this.registry.get('cpX') as number | undefined) ?? this.ents.spawnX)
    const spawnY = ((this.registry.get('cpY') as number | undefined) ?? this.ents.spawnY)
    this.player = new Player(this, spawnX, spawnY, this.bullets)
    this.player.powerUp = this.registry.get('power') === true

    this.cameras.main.startFollow(this.player, true, 0.1, 0.1)

    this.spawnCheckpoint()
    this.spawnEnemies()
    this.spawnEnergyPickups()
    this.spawnCapsules()

    this.cursors = this.input.keyboard!.createCursorKeys()
    this.shootKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.Z)
    this.muteKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.M)
    this.pauseKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.P)
    this.escKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ESC)

    // Collide with BOTH layers: ground is the walkable floor, platforms the floating slabs.
    this.physics.add.collider(this.player, this.ground)
    this.physics.add.collider(this.player, this.platforms)
    this.physics.add.collider(this.enemies, this.ground)
    this.physics.add.collider(this.enemies, this.platforms)
    this.physics.add.collider(this.bullets, this.ground)
    this.physics.add.collider(this.bullets, this.platforms)
    this.physics.add.overlap(
      this.bullets,
      this.enemies,
      this.handleBulletHitEnemy as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    )
    this.physics.add.overlap(
      this.player,
      this.enemies,
      this.handlePlayerHitEnemy as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    )
    this.physics.add.overlap(
      this.player,
      this.orbs,
      this.handlePlayerCollectOrb as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    )
    this.physics.add.overlap(
      this.player,
      this.enemyBullets,
      this.handlePlayerHitByEnemyBullet as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    )
    // Un projectile ennemi qui touche le sol est désactivé (explosion) — sauf
    // les ondes de choc du boss, qui voyagent le long du sol (kind 'shockwave').
    // Le contexte `this` est obligatoire : sans lui, `this.spawnSparks` n'existe
    // pas dans le callback (this = monde arcade).
    this.physics.add.collider(
      this.enemyBullets,
      this.ground,
      this.handleEnemyBulletGround as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    )
    this.physics.add.collider(
      this.enemyBullets,
      this.platforms,
      this.handleEnemyBulletGround as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    )

    // Boss HP bar must exist before the boss spawns (spawn draws it once).
    // Top-right placement so it never covers the player.
    this.bossBar = this.add.graphics().setScrollFactor(0).setDepth(200)
    this.bossName = this.add.text(this.cameras.main.width - 12, 4, this.stage.boss, {
      fontSize: '8px', color: '#ff9d9d', fontFamily: 'monospace', fontStyle: 'bold', letterSpacing: 1,
    }).setOrigin(1, 0).setScrollFactor(0).setDepth(200).setVisible(false)
    this.spawnBoss()

    // HUD
    this.hpBar = this.add.graphics().setScrollFactor(0).setDepth(200)
    this.weBar = this.add.graphics().setScrollFactor(0).setDepth(200)
    this.add.text(6, 3, 'HP', {
      fontSize: '7px', color: '#9fb4d8', fontFamily: 'monospace', fontStyle: 'bold',
    }).setScrollFactor(0).setDepth(200)
    this.add.text(6, 14, this.stage.name, {
      fontSize: '8px', color: '#a9b3cf', fontFamily: 'monospace', letterSpacing: 1,
    }).setScrollFactor(0).setDepth(200)

    this.add.text(6, 24, isTouchUI() ? '◀▶ MOVE A JUMP B=CHARGE ⏸=PAUSE' : '←→:move ↑:jump Z:charge P:pause', {
      fontSize: '7px', color: '#5a6280', fontFamily: 'monospace',
    }).setScrollFactor(0).setDepth(200)

    this.powerText = this.add.text(6, 34, '', {
      fontSize: '7px', color: '#ff9d8a', fontFamily: 'monospace', fontStyle: 'bold', letterSpacing: 3,
    }).setScrollFactor(0).setDepth(200).setVisible(false)
    this.powerText.setShadow(0, 0, '#ff5546', 10, true, true)

    // Rangée d'icônes du pouvoir (signature + boosts) — remplace l'ancien texte.
    for (let i = 0; i < 6; i++) {
      this.compIcons.push(
        this.add.image(this.cameras.main.width - 12, 34, 'icon-tir')
          .setScrollFactor(0).setDepth(200).setScale(0.9).setVisible(false),
      )
    }

    // Lives (mini hero icons)
    const lives = (this.registry.get('lives') as number) ?? 3
    for (let i = 0; i < lives; i++) {
      this.add.image(8 + i * 13, 44, "player", 0)
        .setScale(0.5).setScrollFactor(0).setDepth(200)
    }

    // Compagnon — drone choisi sur l'écran de sélection
    this.compData = getCompanion(this.registry.get('companion'))
    this.compSignature = this.compData.signature
    this.companion = this.add.image(this.player.x - 20, this.player.y - 30, this.compData.texture)
      .setTint(this.compData.tint).setScale(1).setDepth(35)
    this.companionGlow = this.add.image(this.companion.x, this.companion.y, 'glow')
      .setTint(this.compData.bubble).setBlendMode(Phaser.BlendModes.ADD).setScale(0.14).setAlpha(0.3).setDepth(34)

    // Audio: musique du stage (thème propre à chaque stage) + mute toggle (M)
    sfx.unlock()
    startMusic('stage', STAGES.findIndex(s => s.id === this.stage.id))
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => { this.ambientEvt?.remove(); stopMusic() })

    if (!this.registry.get('briefed')) {
      this.registry.set('briefed', true)
      this.time.delayedCall(600, () => this.showBriefing())
    }

    drawVignette(this, 0.45)
    this.cameras.main.fadeIn(300, 0, 0, 0)

    // Apparition : matérialisation + courte invulnérabilité (spawn / respawn).
    this.spawnTeleportEffect(this.player.x, this.player.y)
    this.player.grantInvulnerability(1300)
  }

  update(_time: number, delta: number) {
    const cam = this.cameras.main
    this.bgFar.tilePositionX = cam.scrollX * 0.15
    this.bgFar.tilePositionY = cam.scrollY * 0.06
    this.bgMid.tilePositionX = cam.scrollX * 0.35
    this.bgMid.tilePositionY = cam.scrollY * 0.12

    if (!this.bossIntroDone && this.player.x > this.ents.bossWarnX) this.bossWarning()

    // Merge keyboard + touch (virtual pad). Touch edges are computed against
    // the previous frame, plus a queue for taps shorter than one rendered
    // frame (low-FPS phones) so no press is ever lost.
    const q = consumeTouchEdges()
    const left = this.cursors.left!.isDown || touchState.left
    const right = this.cursors.right!.isDown || touchState.right
    const jump = Phaser.Input.Keyboard.JustDown(this.cursors.up!) || (touchState.jump && !this.tPrevJump) || q.jump
    const jumpHeld = this.cursors.up!.isDown || touchState.jump
    const shootHeld = this.shootKey.isDown || touchState.shoot
    const shootPressed = Phaser.Input.Keyboard.JustDown(this.shootKey) || (touchState.shoot && !this.tPrevShoot) || q.shoot
    this.tPrevJump = touchState.jump
    this.tPrevShoot = touchState.shoot

    // Pause : touche P/ESC au clavier, bouton ⏸ du pad tactile sur mobile.
    if (q.pause || Phaser.Input.Keyboard.JustDown(this.pauseKey) || Phaser.Input.Keyboard.JustDown(this.escKey)) {
      sfx.checkpoint()
      this.scene.launch('PauseScene')
      this.scene.pause()
      return
    }

    // Mort par chute : tomber dans un trou = perte d'une vie (respawn checkpoint).
    if (!this.respawning && !this.player.isDead() && this.player.y > WORLD_H + 30) {
      this.playerFall()
      return
    }

    if (Phaser.Input.Keyboard.JustDown(this.muteKey)) {
      const m = sfx.toggleMute()
      if (!this.muteToast) {
        this.muteToast = this.add.text(this.cameras.main.width - 30, 60, '', {
          fontSize: '14px', color: '#9fb4d8', fontFamily: 'monospace',
        }).setOrigin(1, 0).setScrollFactor(0).setDepth(300)
      }
      this.muteToast.setText(m ? 'SOUND OFF' : 'SOUND ON').setAlpha(1)
      this.tweens.add({ targets: this.muteToast, alpha: 0, duration: 900, delay: 500 })
    }

    const weapon = (this.registry.get('weapon') as WeaponId | undefined) ?? 'buster'
    this.player.weapon = weapon
    // HUD : nom de l'arme de boss équipée (masqué si BUSTER).
    const wname = weapon === 'buster' ? '' : WEAPONS[weapon].name
    if ((this.powerText.text as string) !== wname) this.powerText.setText(wname).setVisible(wname !== '')
    this.player.update(left, right, jump, jumpHeld, shootPressed, shootHeld, delta)
    this.drawHpBar()
    if (weapon !== 'buster') this.drawWeBar()
    else this.weBar.clear()

    // Energy orbs: gentle magnet toward the player when close.
    this.orbs.children.iterate((child) => {
      const orb = child as Phaser.Physics.Arcade.Image
      if (!orb?.active) return true
      const body = orb.body as Phaser.Physics.Arcade.Body
      const dx = this.player.x - orb.x
      const dy = this.player.y - orb.y
      const d = Math.hypot(dx, dy)
      if (d < 56 && d > 1) body.setVelocity((dx / d) * 64, (dy / d) * 64)
      else body.setVelocity(0, 0)
      return true
    })

    this.enemies.children.iterate((child) => {
      const enemy = child as unknown as StageEnemy
      if (!enemy.active) return true
      // Gelé (arme CRYO) : ne bouge plus brièvement.
      const frozenUntil = (child as unknown as { getData?: (k: string) => unknown }).getData?.('frozenUntil') as number | undefined
      if (frozenUntil && frozenUntil > this.time.now) {
        const body = (child as Phaser.Physics.Arcade.Sprite).body as Phaser.Physics.Arcade.Body
        body.setVelocity(0, 0)
        return true
      }
      enemy.update(delta)
      return true
    })

    if (this.bossActive && this.bossIntroDone && this.boss?.active) {
      // Le boss n'entre en action qu'après son écran WARNING (x>660) :
      // sinon il marche vers le joueur dès le spawn et rencontre le niveau.
      this.boss.update(delta)
      this.drawBossBar()
      this.bossTauntTimer -= delta
      if (this.bossTauntTimer <= 0 && !this.bossTauntBusy) {
        this.bossTauntTimer = 9500
        void this.requestBossTaunt('combat')
      }
    }

    // ORION suit le joueur avec un léger retard
    this.compT += delta
    const tx = this.player.x + (this.player.flipX ? 20 : -20)
    const ty = this.player.y - 30 + Math.sin(this.compT / 420) * 4
    this.companion.x += (tx - this.companion.x) * 0.08
    this.companion.y += (ty - this.companion.y) * 0.08
    this.companionGlow.setPosition(this.companion.x, this.companion.y)
    this.compCooldown -= delta
    if (this.player.getHealth() <= 3 && !this.compSaid.has('lowhp') && this.compCooldown <= 0) {
      this.compSaid.add('lowhp')
      void this.companionSay('lowhp')
    }
    this.applyCompanionPowers(delta)
  }

  /** Pouvoirs de compagnon (capsules) : tir drone, bouclier, soin, cadence rapide. */
  private applyCompanionPowers(delta: number) {
    const powers = new Set<string>(this.registry.get('companionPowers') ?? [])
    const boost = powers.has('puissance') ? 1 : 0
    this.player.fireMult = powers.has('rapide') ? 0.55 : 1

    // SIGNATURE : le pouvoir de combat du compagnon CHOISI (tir/bouclier/explosion),
    // toujours actif. 'puissance' le renforce.
    const sig = this.compSignature
    if (sig === 'tir') {
      this.compShootT -= delta
      if (this.compShootT <= 0) {
        this.compShootT = 1500 - boost * 300
        const e = this.enemies.getFirstAlive() as Phaser.Physics.Arcade.Sprite | null
        if (e?.active) this.fireCompanionBullet(e, 2 + boost, 'buster', COMBAT.tir.tint)
      }
    } else if (sig === 'explosion') {
      this.compShootT -= delta
      if (this.compShootT <= 0) {
        this.compShootT = 2000 - boost * 300
        const e = this.enemies.getFirstAlive() as Phaser.Physics.Arcade.Sprite | null
        if (e?.active) this.fireCompanionBullet(e, 2 + boost, 'venom', COMBAT.explosion.tint)
      }
    } else if (sig === 'bouclier') {
      this.compShieldT -= delta
      if (this.compShieldT <= 0) {
        this.compShieldT = 9000 - boost * 1500
        this.player.grantInvulnerability(1600 + boost * 400)
        this.spawnShieldBubble()
      }
    }
    // SOIN : le compagnon te soigne quand tu es bas.
    if (powers.has('soin')) {
      this.compHealT -= delta
      if (this.compHealT <= 0) {
        if (this.player.getHealth() <= 4) {
          this.compHealT = 6000 - boost * 1500
          this.player.heal(1)
          this.spawnCollectBurst(this.player.x, this.player.y, 0x7dfca2)
          sfx.collect()
        } else {
          this.compHealT = 500
        }
      }
    }

    // HUD compagnon : icônes de pouvoir (signature + boosts) au lieu du texte.
    this.renderCompIcons(powers)
  }

  /** Affiche la rangée d'icônes de pouvoir (signature en premier, puis les boosts). */
  private renderCompIcons(powers: Set<string>) {
    const ids = [this.compSignature, ...[...powers]]
    const gap = 14
    const n = Math.min(ids.length, this.compIcons.length)
    const startX = this.cameras.main.width - 12 - n * gap
    for (let i = 0; i < this.compIcons.length; i++) {
      const ic = this.compIcons[i]
      if (i < n) {
        // Ne change la texture que si l'icône a changé (évite setTexture à chaque frame).
        if ((ic.texture.key ?? '') !== `icon-${ids[i]}`) ic.setTexture(`icon-${ids[i]}`)
        ic.setPosition(startX + i * gap, 34).setVisible(true)
      } else {
        ic.setVisible(false)
      }
    }
  }

  /** Tire une balle de compagnon vers un ennemi (couleur + sprite = pouvoir). */
  private fireCompanionBullet(e: Phaser.Physics.Arcade.Sprite, dmg: number, weapon: WeaponId, tint: number) {
    const b = this.bullets.get(this.companion.x, this.companion.y - 4, 'bullet') as Bullet
    if (!b) return
    const dir = Math.sign(e.x - this.companion.x) || 1
    b.activate(dir, 'normal', dmg, weapon)
    const ang = Math.atan2(e.y - this.companion.y, e.x - this.companion.x)
    b.setVelocity(Math.cos(ang) * 160, Math.sin(ang) * 160)
    b.setTint(tint)
    // Sprite de tir PROPRE au compagnon : ORION = dard cyan, NOVA = orbe rose.
    const shotTex = this.compSignature === 'explosion' ? 'comp-nova-missile' : 'comp-orion-shot'
    b.setTexture(shotTex)
    const bs = b.body as Phaser.Physics.Arcade.Body
    bs.setSize(Math.max(8, Math.round(b.width * 0.7)), Math.max(6, Math.round(b.height * 0.7)))
    this.spawnCompMuzzle(tint)
    // Recul du compagnon quand il tire (animation de tir).
    this.tweens.killTweensOf(this.companion)
    this.tweens.add({ targets: this.companion, scale: 0.86, duration: 60, yoyo: true, ease: 'Quad.Out' })
    sfx.turretShot()
  }

  /** Petit flash de bouche (muzzle) teinté au pouvoir, additive. */
  private spawnCompMuzzle(tint: number) {
    const m = this.add.image(this.companion.x, this.companion.y - 4, 'comp-muzzle')
      .setTint(tint).setBlendMode(Phaser.BlendModes.ADD).setDepth(36).setScale(0.5).setAlpha(0.9)
    this.tweens.add({ targets: m, scale: 1.15, alpha: 0, duration: 150, onComplete: () => m.destroy() })
  }

  private createAnimations() {
    if (!this.anims.exists('player-idle')) {
      this.anims.create({
        key: 'player-idle',
        frames: this.anims.generateFrameNumbers('player', { start: 0, end: 1 }),
        frameRate: 3,
        repeat: -1,
      })
    }
    if (!this.anims.exists('player-run')) {
      this.anims.create({
        key: 'player-run',
        frames: this.anims.generateFrameNumbers('player', { start: 2, end: 5 }),
        frameRate: 12,
        repeat: -1,
      })
    }
    if (!this.anims.exists('player-jump')) {
      this.anims.create({
        key: 'player-jump',
        frames: this.anims.generateFrameNumbers('player', { frames: [6] }),
        frameRate: 10,
        repeat: -1,
      })
    }
    if (!this.anims.exists('player-fall')) {
      this.anims.create({
        key: 'player-fall',
        frames: this.anims.generateFrameNumbers('player', { frames: [7] }),
        frameRate: 10,
        repeat: -1,
      })
    }
    // Anims d'ennemis recréées à chaque stage : la texture 'enemy'/'flyer'/'boss'
    // change selon le stage, il faut donc régénérer les frames (sinon les anims
    // gardaient les frames de la première texture chargée).
    this.anims.remove('enemy-walk')
    this.anims.create({
      key: 'enemy-walk',
      frames: this.anims.generateFrameNumbers('enemy', { start: 0, end: 1 }),
      frameRate: 6,
      repeat: -1,
    })
    this.anims.remove('flyer-fly')
    this.anims.create({
      key: 'flyer-fly',
      frames: this.anims.generateFrameNumbers('flyer', { start: 0, end: 1 }),
      frameRate: 10,
      repeat: -1,
    })
    this.anims.remove('boss-idle')
    this.anims.create({
      key: 'boss-idle',
      frames: this.anims.generateFrameNumbers('boss', { start: 0, end: 1 }),
      frameRate: 4,
      repeat: -1,
    })
    this.anims.remove('charger-anim')
    this.anims.create({
      key: 'charger-anim',
      frames: this.anims.generateFrameNumbers('charger', { start: 0, end: 1 }),
      frameRate: 8,
      repeat: -1,
    })
    this.anims.remove('spitter-anim')
    this.anims.create({
      key: 'spitter-anim',
      frames: this.anims.generateFrameNumbers('spitter', { start: 0, end: 1 }),
      frameRate: 8,
      repeat: -1,
    })
  }

  private createTilemap() {
    const map = this.make.tilemap({ key: 'level' })
    const tileset = map.addTilesetImage('tileset', 'tileset')
    this.ground = map.createLayer('ground', tileset!)!
    this.platforms = map.createLayer('platforms', tileset!)!

    this.ground.setCollisionByExclusion([-1])
    this.platforms.setCollisionByExclusion([-1])

    // Décor distinct par niveau : teinte l'accent du stage sur le sol/les dalles
    // (multiplication) — chaque secteur prend la couleur de son univers.
    this.ground.setTint(this.stage.accent)
    this.platforms.setTint(this.stage.accent)
  }

  private createBackground() {
    const { width, height } = this.cameras.main

    // Sky gradient (static, tinted per stage).
    const sky = this.add.graphics()
    sky.fillGradientStyle(this.stage.skyTop, this.stage.skyTop, this.stage.skyBottom, this.stage.skyBottom, 1)
    sky.fillRect(0, 0, width, height)
    sky.setScrollFactor(0).setDepth(-30)

    // Parallax skyline layers (per-stage HD images, infinite horizontal tiling).
    this.bgFar = this.add.tileSprite(width / 2, height / 2, width, height, `bg-far-${this.stage.id}`)
      .setScrollFactor(0).setDepth(-20)
    this.bgMid = this.add.tileSprite(width / 2, height / 2, width, height, `bg-mid-${this.stage.id}`)
      .setScrollFactor(0).setDepth(-12)

    // Soft accent haze on the horizon.
    this.add.image(width / 2, height * 0.66, 'haze')
      .setScrollFactor(0).setDepth(-10)
      .setTint(this.stage.accent).setAlpha(0.22)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setScale(1, 1)

    // Slow ambient light motes drifting upward (screen space).
    this.add.particles(0, 0, 'glow', {
      x: { min: 0, max: width },
      y: { min: 0, max: height },
      lifespan: 8000,
      speedY: { min: -5, max: -1.5 },
      speedX: { min: -2, max: 2 },
      scale: { start: 0.015, end: 0.045 },
      alpha: { start: 0.16, end: 0 },
      frequency: 550,
      blendMode: Phaser.BlendModes.ADD,
      tint: [this.stage.accent, 0xffffff, 0x9fb4d8],
    }).setScrollFactor(0).setDepth(-5)
  }

  // ------------------------------------------------------------------
  // AMBIANCE DE FOND : événements décoratifs propres à chaque stage.
  // Purement visuels (depth -3, scrollFactor 0, derrière le niveau).
  // ------------------------------------------------------------------
  private static readonly AMBIENT_PERIOD: Record<string, number> = {
    'neon-city': 2600,
    'toxic-plant': 1100,
    'scorched-desert': 1900,
    'frost-lab': 1400,
    'sky-fortress': 3000,
  }

  /** Lance le générateur d'ambiance du stage (auto-détruit au shutdown). */
  private setupAmbient() {
    const period = GameScene.AMBIENT_PERIOD[this.stage.id]
    if (!period) return
    // premier événement assez vite, puis en boucle.
    this.time.delayedCall(500, () => this.spawnAmbient())
    this.ambientEvt = this.time.addEvent({ delay: period, loop: true, callback: () => this.spawnAmbient() })
  }

  private spawnAmbient() {
    switch (this.stage.id) {
      case 'neon-city': this.ambientExplosion(); break
      case 'toxic-plant': this.ambientDrip(); break
      case 'scorched-desert': this.ambientEmber(); break
      case 'frost-lab': this.ambientSnow(); break
      case 'sky-fortress': this.ambientLightning(); break
    }
  }

  /** Néon City : explosion lointaine (cœur + halo + onde + fumée + étincelles). */
  private ambientExplosion() {
    const { width, height } = this.cameras.main
    const x = Phaser.Math.Between(40, width - 40)
    const y = Phaser.Math.Between(Math.round(height * 0.16), Math.round(height * 0.4))
    // cœur blanc chaud (culmine vite)
    const core = this.add.image(x, y, 'glow')
      .setTint(0xffffff).setBlendMode(Phaser.BlendModes.ADD).setDepth(-3).setScrollFactor(0).setScale(0.6).setAlpha(1)
    this.tweens.add({ targets: core, scale: 1.9, alpha: 0, duration: 300, ease: 'Cubic.Out', onComplete: () => core.destroy() })
    // halo orange qui gonfle (gros dès le départ)
    const halo = this.add.image(x, y, 'glow')
      .setTint(0xff9a3c).setBlendMode(Phaser.BlendModes.ADD).setDepth(-3).setScrollFactor(0).setScale(1).setAlpha(0.95)
    this.tweens.add({ targets: halo, scale: 3, alpha: 0, duration: 560, ease: 'Cubic.Out', onComplete: () => halo.destroy() })
    // onde de choc
    const ring = this.add.image(x, y, 'glow')
      .setTint(0xffd9a0).setBlendMode(Phaser.BlendModes.ADD).setDepth(-3).setScrollFactor(0).setScale(0.5).setAlpha(0.6)
    this.tweens.add({ targets: ring, scale: 3.7, alpha: 0, duration: 480, ease: 'Cubic.Out', onComplete: () => ring.destroy() })
    // fumée
    for (let i = 0; i < 6; i++) {
      const s = this.add.image(x + Phaser.Math.Between(-18, 18), y + Phaser.Math.Between(-12, 10), 'orb')
        .setTint(0x2a2f3a).setDepth(-3).setScrollFactor(0).setScale(0.28).setAlpha(0.6)
      this.tweens.add({ targets: s, x: s.x + Phaser.Math.Between(-30, 30), y: s.y - Phaser.Math.Between(12, 44), scale: 0.6, alpha: 0, duration: Phaser.Math.Between(850, 1450), ease: 'Cubic.Out', onComplete: () => s.destroy() })
    }
    // étincelles
    const p = this.add.particles(x, y, 'glow', {
      speed: { min: 60, max: 180 }, scale: { start: 0.16, end: 0 }, lifespan: 680, emitting: false,
      blendMode: Phaser.BlendModes.ADD, tint: [0xffe9a8, 0xff9a3c, 0xffffff],
    }).setDepth(-3).setScrollFactor(0)
    p.explode(16)
    this.time.delayedCall(1000, () => p.destroy())
  }

  /** Toxic Plant : gouttes de slime qui tombent du plafond et éclaboussent. */
  private ambientDrip() {
    const { width, height } = this.cameras.main
    const x = Phaser.Math.Between(40, width - 40)
    const bottom = height * 0.52
    const d = this.add.image(x, -10, 'orb')
      .setTint(0x7dfca2).setBlendMode(Phaser.BlendModes.ADD).setDepth(-3).setScrollFactor(0)
      .setScale(0.55).setAlpha(0.95)
    this.tweens.add({
      targets: d, y: bottom, duration: Phaser.Math.Between(950, 1500), ease: 'Quad.In',
      onComplete: () => {
        const splash = this.add.image(x, bottom, 'glow')
          .setTint(0x7dfca2).setBlendMode(Phaser.BlendModes.ADD).setDepth(-3).setScrollFactor(0)
          .setScale(0.4).setAlpha(0.8)
        this.tweens.add({ targets: splash, scale: 1.6, alpha: 0, duration: 460, onComplete: () => splash.destroy() })
        // petites gouttelettes d'éclaboussure
        for (let i = 0; i < 5; i++) {
          const sp = this.add.image(x, bottom, 'orb')
            .setTint(0x7dfca2).setBlendMode(Phaser.BlendModes.ADD).setDepth(-3).setScrollFactor(0).setScale(0.22).setAlpha(0.75)
          this.tweens.add({ targets: sp, x: sp.x + Phaser.Math.Between(-20, 20), y: bottom - Phaser.Math.Between(10, 30), alpha: 0, duration: 460, onComplete: () => sp.destroy() })
        }
        d.destroy()
      },
    })
  }

  /** Scorched Desert : braises qui montent. */
  private ambientEmber() {
    const { width, height } = this.cameras.main
    const n = Phaser.Math.Between(3, 5)
    for (let i = 0; i < n; i++) {
      const x = Phaser.Math.Between(0, width)
      const y = height * Phaser.Math.Between(0.5, 0.85)
      const e = this.add.image(x, y, 'orb')
        .setTint(0xffb37a).setBlendMode(Phaser.BlendModes.ADD).setDepth(-3).setScrollFactor(0)
        .setScale(0.1).setAlpha(0.7)
      this.tweens.add({ targets: e, y: y - Phaser.Math.Between(60, 130), x: e.x + Phaser.Math.Between(-18, 18), alpha: 0, duration: Phaser.Math.Between(1100, 1900), onComplete: () => e.destroy() })
    }
  }

  /** Frost Lab : flocons qui tombent. */
  private ambientSnow() {
    const { width, height } = this.cameras.main
    const n = Phaser.Math.Between(4, 7)
    for (let i = 0; i < n; i++) {
      const x = Phaser.Math.Between(0, width)
      const y = Phaser.Math.Between(-10, Math.round(height * 0.2))
      const s = this.add.image(x, y, 'orb')
        .setTint(0xdbe9ff).setDepth(-3).setScrollFactor(0).setScale(0.07).setAlpha(0.85)
      this.tweens.add({ targets: s, y: y + Phaser.Math.Between(80, 150), x: s.x + Phaser.Math.Between(-16, 16), alpha: 0, duration: Phaser.Math.Between(1400, 2400), onComplete: () => s.destroy() })
    }
  }

  /** Sky Fortress : éclairs de tempête qui flashent dans le ciel. */
  private ambientLightning() {
    const { width, height } = this.cameras.main
    for (let i = 0; i < 3; i++) {
      const x = Phaser.Math.Between(0, width)
      const y = Phaser.Math.Between(Math.round(height * 0.1), Math.round(height * 0.3))
      const f = this.add.image(x, y, 'glow')
        .setTint(0xffffff).setBlendMode(Phaser.BlendModes.ADD).setDepth(-3).setScrollFactor(0)
        .setScale(0.8).setAlpha(0)
      this.tweens.add({ targets: f, alpha: 0.9, scale: 1.25, duration: 60, yoyo: true, repeat: 1, onComplete: () => f.destroy() })
    }
  }

  /** Couche d'ambiance CONTINUE (pluie, vapeur, cendres, brume, débris) par stage. */
  private setupAmbientContinuous() {
    const { width, height } = this.cameras.main
    const dep = -4
    switch (this.stage.id) {
      case 'neon-city': {
        // pluie : dense, cyan, en travers de l'écran (avec les explosions).
        this.add.particles(0, -8, 'rain', {
          x: { min: 0, max: width }, y: -8,
          speedY: { min: 150, max: 260 }, speedX: { min: -30, max: -12 },
          lifespan: 2200, scale: { min: 0.7, max: 1.25 }, alpha: { start: 0.55, end: 0 },
          quantity: 3, frequency: 60, blendMode: Phaser.BlendModes.ADD, tint: 0x7fd4ff,
        }).setScrollFactor(0).setDepth(dep)
        // fumée grise qui dérive lentement au-dessus de la ville.
        this.add.particles(0, -10, 'glow', {
          x: { min: 0, max: width }, y: -10,
          speedY: { min: -8, max: -2 }, speedX: { min: -6, max: 6 },
          lifespan: 5000, scale: { start: 0.4, end: 1 }, alpha: { start: 0.05, end: 0 },
          quantity: 1, frequency: 300, tint: 0x39404e,
        }).setScrollFactor(0).setDepth(dep)
        break
      }
      case 'toxic-plant': {
        // bulles de gaz toxique qui montent depuis le sol (avec les gouttes de slime).
        this.add.particles(0, height + 6, 'orb', {
          x: { min: 0, max: width }, y: height + 6,
          speedY: { min: -26, max: -54 }, speedX: { min: -8, max: 8 },
          lifespan: 3200, scale: { start: 0.12, end: 0.46 }, alpha: { start: 0.5, end: 0 },
          quantity: 1, frequency: 190, blendMode: Phaser.BlendModes.ADD, tint: [0x7dfca2, 0xb9ffce],
        }).setScrollFactor(0).setDepth(dep)
        // vapeur/smoke : panache vert qui monte.
        this.add.particles(0, height + 8, 'glow', {
          x: { min: 0, max: width }, y: height + 8,
          speedY: { min: -16, max: -5 }, speedX: { min: -10, max: 10 },
          lifespan: 4200, scale: { start: 0.5, end: 1.3 }, alpha: { start: 0.08, end: 0 },
          quantity: 1, frequency: 180, blendMode: Phaser.BlendModes.ADD, tint: 0x6ee7a0,
        }).setScrollFactor(0).setDepth(dep)
        break
      }
      case 'scorched-desert': {
        // cendres/braises qui dérivent en travers (avec les braises montantes).
        this.add.particles(0, height + 4, 'orb', {
          x: { min: 0, max: width }, y: height + 4,
          speedY: { min: -32, max: -14 }, speedX: { min: 18, max: 46 },
          lifespan: 3600, scale: { start: 0.08, end: 0.22 }, alpha: { start: 0.5, end: 0 },
          quantity: 1, frequency: 160, blendMode: Phaser.BlendModes.ADD, tint: [0xffd9b0, 0xff9a3c],
        }).setScrollFactor(0).setDepth(dep)
        // halo de chaleur qui miroite à l'horizon.
        this.addHeatShimmer()
        break
      }
      case 'frost-lab': {
        // brume froide qui monte doucement (avec la neige).
        this.add.particles(0, height + 4, 'glow', {
          x: { min: 0, max: width }, y: { min: height * 0.5, max: height + 4 },
          speedY: { min: -10, max: -2 }, speedX: { min: -16, max: 12 },
          lifespan: 5200, scale: { start: 0.5, end: 1.2 }, alpha: { start: 0.06, end: 0 },
          quantity: 1, frequency: 240, blendMode: Phaser.BlendModes.ADD, tint: 0xdbe9ff,
        }).setScrollFactor(0).setDepth(dep)
        // vapeur froide : petits nuages qui dérivent.
        this.add.particles(0, height * 0.55, 'glow', {
          x: { min: 0, max: width }, y: { min: height * 0.55, max: height + 4 },
          speedX: { min: -20, max: 16 }, speedY: { min: -4, max: 0 },
          lifespan: 4800, scale: { start: 0.9, end: 1.6 }, alpha: { start: 0.07, end: 0 },
          quantity: 1, frequency: 320, blendMode: Phaser.BlendModes.ADD, tint: 0xffffff,
        }).setScrollFactor(0).setDepth(dep)
        break
      }
      case 'sky-fortress': {
        // débris sombres plus nombreux qui dérivent (avec les éclairs).
        this.add.particles(0, 0, 'orb', {
          x: { min: 0, max: width }, y: { min: height * 0.12, max: height * 0.62 },
          speedX: { min: -36, max: -12 }, speedY: { min: -4, max: 4 },
          lifespan: 6500, scale: { start: 0.2, end: 0.44 }, alpha: { start: 0.4, end: 0 },
          quantity: 1, frequency: 280, tint: 0x272c38,
        }).setScrollFactor(0).setDepth(dep)
        // lueurs énergétiques flottantes.
        this.add.particles(0, 0, 'glow', {
          x: { min: 0, max: width }, y: { min: height * 0.15, max: height * 0.5 },
          speedY: { min: -14, max: -4 }, speedX: { min: -8, max: 8 },
          lifespan: 2600, scale: { start: 0.07, end: 0.02 }, alpha: { start: 0.4, end: 0 },
          quantity: 1, frequency: 240, blendMode: Phaser.BlendModes.ADD, tint: [0xf472b6, 0x9fb4d8],
        }).setScrollFactor(0).setDepth(dep)
        break
      }
    }
  }

  /** Halo de chaleur : bandes orange qui miroitent à l'horizon (scorched-desert). */
  private addHeatShimmer() {
    const { width, height } = this.cameras.main
    for (let i = 0; i < 3; i++) {
      const band = this.add.rectangle(width / 2, height * 0.58 + i * 5, width, 3, 0xffb37a)
        .setBlendMode(Phaser.BlendModes.ADD).setScrollFactor(0).setDepth(-4)
      this.tweens.add({
        targets: band, alpha: { from: 0.03, to: 0.1 }, y: band.y + 3,
        duration: 800 + i * 180, yoyo: true, repeat: -1, ease: 'Sine.InOut',
      })
    }
  }

  /** Modern segmented HP bar (screen-fixed HUD). */
  private drawHpBar() {
    const hp = this.player.getHealth()
    const max = 10
    const x = 20, y = 5, w = 84, h = 6
    const frac = Math.max(0, hp / max)
    this.hpBar.clear()
    this.hpBar.fillStyle(0x0a0d16, 0.75).fillRoundedRect(x - 2, y - 2, w + 4, h + 4, 3)
    this.hpBar.lineStyle(1, 0x3a4358, 1).strokeRoundedRect(x - 2, y - 2, w + 4, h + 4, 3)
    this.hpBar.fillStyle(0x1a2030, 1).fillRoundedRect(x, y, w, h, 3)
    if (frac > 0) {
      const fw = Math.max(10, w * frac)
      this.hpBar.fillGradientStyle(0xff8a94, 0xff2436, 0xa11024, 0x6e0f16, 1)
      this.hpBar.fillRoundedRect(x, y, fw, h, 4)
      this.hpBar.fillStyle(0xffffff, 0.28).fillRoundedRect(x + 1, y + 1, Math.max(2, fw - 2), 2, 1)
    }
    this.hpBar.fillStyle(0x0a0d16, 0.5)
    for (let i = 1; i < 10; i++) this.hpBar.fillRect(x + (w / 10) * i, y, 1, h)
  }

  /** Barre d'énergie du canon war (HUD). */
  private drawWeBar() {
    // Sous le bloc HP / vie : on évite le pouvoir (y=34) et les icônes de vie (y=44).
    const x = 20, y = 56, w = 84, h = 5
    const frac = Math.max(0, this.player.getWe() / this.player.getWeMax())
    this.weBar.clear()
    this.weBar.fillStyle(0x0a0d16, 0.75).fillRoundedRect(x - 2, y - 2, w + 4, h + 4, 3)
    this.weBar.fillStyle(0x1a2030, 1).fillRect(x, y, w, h)
    if (frac > 0) {
      this.weBar.fillGradientStyle(0xff8a94, 0xff2436, 0xa11024, 0x6e0f16, 1)
      this.weBar.fillRect(x, y, Math.max(2, w * frac), h)
    }
    this.weBar.fillStyle(0x0a0d16, 0.5)
    for (let i = 1; i < 7; i++) this.weBar.fillRect(x + (w / 7) * i, y, 1, h)
  }

  /** Yellow-white impact sparks at a world position. */
  spawnSparks(x: number, y: number) {
    const p = this.add.particles(x, y, 'glow', {
      speed: { min: 30, max: 70 },
      scale: { start: 0.05, end: 0 },
      lifespan: 260,
      tint: [0xffffff, 0xfacc15, 0xfb923c],
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    })
    p.explode(8)
    this.time.delayedCall(320, () => p.destroy())
  }

  /** Orange explosion burst + shockwave ring at a world position. */
  spawnExplosion(x: number, y: number, big = false) {
    const p = this.add.particles(x, y, 'glow', {
      speed: { min: big ? 30 : 20, max: big ? 90 : 60 },
      scale: { start: big ? 0.15 : 0.1, end: 0 },
      lifespan: big ? 620 : 420,
      tint: [0xfbbf24, 0xf97316, 0xef4444],
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    })
    p.explode(big ? 24 : 12)
    this.time.delayedCall(big ? 680 : 480, () => p.destroy())

    const ring = this.add.circle(x, y, big ? 6 : 3).setStrokeStyle(1.5, 0xffffff, 1)
    this.tweens.add({
      targets: ring,
      radius: big ? 48 : 22,
      alpha: 0,
      duration: big ? 380 : 240,
      ease: 'Cubic.Out',
      onComplete: () => ring.destroy(),
    })
    this.cameras.main.shake(big ? 260 : 90, big ? 0.006 : 0.0025)
  }

  /** Gray dust puff at the player's feet on hard landings. */
  spawnLandingDust(x: number, y: number) {
    const p = this.add.particles(x, y, 'glow', {
      speed: { min: 10, max: 28 },
      angle: { min: 200, max: 340 },
      scale: { start: 0.05, end: 0 },
      lifespan: 280,
      alpha: { start: 0.5, end: 0 },
      tint: [0x94a3b8, 0xcbd5e1],
      emitting: false,
    })
    p.explode(5)
    this.time.delayedCall(340, () => p.destroy())
  }

  /** One-frame muzzle flash at the buster tip. */
  spawnMuzzleFlash(x: number, y: number, scale = 0.1, flame = false) {
    const halo = this.add.image(x, y, 'glow')
      .setTint(flame ? 0xffb37a : 0xffb3b8).setBlendMode(Phaser.BlendModes.ADD).setScale(scale).setDepth(50)
    const core = this.add.circle(x, y, 1.5 + scale * 8, 0xffffff).setBlendMode(Phaser.BlendModes.ADD).setDepth(51)
    this.tweens.add({
      targets: [halo, core], alpha: 0, scale: 1.9, duration: 80,
      onComplete: () => { halo.destroy(); core.destroy() },
    })
  }

  /** Apparition du joueur : halo doux + motes ascendantes + onde + flash. */
  private spawnTeleportEffect(x: number, y: number) {
    // Halo doux (pas une colonne dure : un beam étiré passait sur le sprite et
    // se lisait comme une barre cyan/verte sur le héros).
    const beam = this.add.image(x, y, 'glow')
      .setBlendMode(Phaser.BlendModes.ADD).setDepth(38)
      .setTint(0xffb3b8).setScale(0.85, 1.5).setAlpha(0)
    this.tweens.add({
      targets: beam, alpha: { from: 0.45, to: 0 }, scale: 1.3, duration: 380,
      ease: 'Cubic.Out', onComplete: () => beam.destroy(),
    })

    // Motes ascendantes (direction 270° ≈ vers le haut).
    const p = this.add.particles(x, y, 'glow', {
      speed: { min: 14, max: 44 },
      angle: { min: 250, max: 290 },
      scale: { start: 0.09, end: 0 },
      lifespan: 540,
      tint: [0xffb3b8, 0xffffff, 0xff2436],
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    })
    p.explode(18)
    this.time.delayedCall(620, () => p.destroy())

    // Onde d'apparition circulaire.
    const ring = this.add.circle(x, y, 4).setStrokeStyle(2, 0xffb3b8, 1)
    this.tweens.add({
      targets: ring, radius: 32, alpha: 0, duration: 400,
      ease: 'Cubic.Out', onComplete: () => ring.destroy(),
    })

    this.cameras.main.flash(220, 160, 200, 255)
  }

  /** Éclat de mort : grosse explosion + onde + flash + débris rouges. */
  private spawnDeathEffect(x: number, y: number) {
    this.cameras.main.flash(240, 255, 90, 80)
    this.spawnExplosion(x, y, true)

    const ring = this.add.circle(x, y, 10).setStrokeStyle(3, 0xff5546, 1)
    this.tweens.add({
      targets: ring, radius: 70, alpha: 0, duration: 520,
      ease: 'Cubic.Out', onComplete: () => ring.destroy(),
    })

    const p = this.add.particles(x, y, 'glow', {
      speed: { min: 40, max: 120 },
      scale: { start: 0.12, end: 0 },
      lifespan: 700,
      tint: [0xff5546, 0xffb37a, 0xffffff],
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    })
    p.explode(28)
    this.time.delayedCall(760, () => p.destroy())
  }

  // ------------------------- Energy orbs -------------------------

  private spawnEnergyPickups() {
    for (const s of this.ents.orbs) this.spawnOrb(s.x, s.y, 'hp')
  }

  spawnOrb(x: number, y: number, kind: 'hp' | 'core') {
    const orb = this.orbs.create(x, y, 'orb') as Phaser.Physics.Arcade.Image
    orb.setDepth(30)
    orb.setData('kind', kind)
    if (kind === 'core') {
      orb.setScale(1.4).setTint(0xff6b5e)
      orb.body!.setSize(8, 8)
      this.tweens.add({ targets: orb, scale: 1.6, duration: 550, yoyo: true, repeat: -1, ease: 'Sine.InOut' })
      this.tweens.add({ targets: orb, alpha: { from: 0.7, to: 1 }, duration: 300, yoyo: true, repeat: -1 })
    } else {
      orb.setScale(1).setTint(0x7dfca2)
      orb.body!.setSize(6, 6)
      this.tweens.add({ targets: orb, alpha: { from: 0.7, to: 1 }, duration: 650, yoyo: true, repeat: -1 })
    }
    return orb
  }

  /** Capsules de compagnon : objets à collecter qui déverrouillent un pouvoir. */
  private spawnCapsules() {
    for (const c of this.ents.capsules ?? []) {
      const tint = getCapsule(c.type).tint
      const cap = this.orbs.create(c.x, c.y, 'capsule') as Phaser.Physics.Arcade.Image
      cap.setDepth(30).setData('kind', 'capsule').setData('capType', c.type)
      cap.setTint(tint)
      cap.body!.setSize(10, 10)
      // Halo lumineux pour bien repérer la capsule.
      const halo = this.add.image(c.x, c.y, 'glow')
        .setTint(tint).setBlendMode(Phaser.BlendModes.ADD).setScale(0.35).setAlpha(0.5).setDepth(29)
      this.tweens.add({ targets: halo, scale: 0.5, alpha: 0.8, duration: 700, yoyo: true, repeat: -1 })
      this.tweens.add({ targets: [cap, halo], y: c.y - 5, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.InOut' })
      this.tweens.add({ targets: cap, angle: 360, duration: 2200, repeat: -1 })
      this.tweens.add({ targets: cap, alpha: { from: 0.6, to: 1 }, duration: 500, yoyo: true, repeat: -1 })
    }
  }

  /** Accorde un pouvoir de compagnon (capsule) + toast + son. */
  private grantCompanionPower(type: string) {
    const def = getCapsule(type)
    const owned = new Set<string>(this.registry.get('companionPowers') ?? [])
    if (owned.has(type)) { sfx.collect(); return }
    owned.add(type)
    this.registry.set('companionPowers', [...owned])
    sfx.powerup()
    if (this.compData) void this.companionSay('power')
    const { width } = this.cameras.main
    const t = this.add.text(width / 2, 74, `${def.name} — ${def.desc}`, {
      fontSize: '10px', color: '#ceffe8', fontFamily: 'monospace', fontStyle: 'bold', letterSpacing: 1,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(300).setAlpha(0).setScale(0.8)
    t.setStroke('#05230f', 3); t.setShadow(0, 0, '#7dfca2', 8, true, true)
    this.tweens.add({ targets: t, alpha: 1, scale: 1, duration: 320, ease: 'Back.Out' })
    this.time.delayedCall(2400, () => this.tweens.add({ targets: t, alpha: 0, duration: 400, onComplete: () => t.destroy() }))
  }

  /** Bulle de protection autour du joueur (pouvoir bouclier). */
  private spawnShieldBubble() {
    // Champ hexagonal (texture BOLT) pulsant autour du joueur esthétique ;
    // l'invulnérabilité réelle est gérée par grantInvulnerability.
    const pulse = this.add.image(this.player.x, this.player.y, 'comp-bolt-field')
      .setBlendMode(Phaser.BlendModes.ADD).setDepth(60).setScale(0.6).setAlpha(0)
    this.tweens.add({ targets: pulse, alpha: 0.9, scale: 1, duration: 180 })
    this.tweens.add({ targets: pulse, alpha: 0, scale: 1.5, duration: 1400, delay: 180, ease: 'Cubic.Out', onComplete: () => pulse.destroy() })
    this.cameras.main.flash(160, 140, 200, 255)
  }

  private spawnCollectBurst(x: number, y: number, tint: number) {
    const p = this.add.particles(x, y, 'glow', {
      speed: { min: 18, max: 48 },
      scale: { start: 0.05, end: 0 },
      lifespan: 300,
      tint: [tint, 0xffffff],
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    })
    p.explode(8)
    this.time.delayedCall(360, () => p.destroy())
  }

  private handlePlayerCollectOrb(
    player: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    orbObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ) {
    const p = player as Player
    const orb = orbObj as Phaser.Physics.Arcade.Image
    if (!orb.active) return
    const kind = orb.getData('kind') as 'hp' | 'core' | 'capsule'
    const ox = orb.x, oy = orb.y
    orb.disableBody(true, true)

    if (kind === 'core') {
      this.absorbBossPower(ox, oy)
      return
    }
    if (kind === 'capsule') {
      this.grantCompanionPower(orb.getData('capType') as string)
      this.spawnCollectBurst(ox, oy, getCapsule(orb.getData('capType') as string).tint)
      return
    }
    p.heal(2)
    p.addWe(6)
    sfx.collect()
    this.spawnCollectBurst(ox, oy, 0x7dfca2)
  }

  /** Boss power absorbed: +1 damage on every shot, flame tint, cycling max-charge. */
  private absorbBossPower(x: number, y: number) {
    // Le pouvoir dépend du gardien vaincu : chaque boss donne SON arme.
    const wpn = BOSS_WEAPON[this.stage.id] ?? 'ram'
    const owned = new Set<string>(this.registry.get('weapons') ?? [])
    owned.add(wpn)
    this.registry.set('weapons', [...owned])
    this.registry.set('weapon', wpn)
    this.player.powerUp = true
    this.registry.set('power', true)
    sfx.powerup()
    void this.companionSay('power')
    this.cameras.main.flash(320, 255, 128, 96)
    this.spawnCollectBurst(x, y, 0xff6b5e)
    this.spawnExplosion(x, y, false)

    const { width } = this.cameras.main
    const t = this.add.text(width / 2, 58, `${WEAPONS[wpn].name} ABSORBED`, {
      fontSize: '12px', color: '#ffb3a8', fontFamily: 'monospace', fontStyle: 'bold', letterSpacing: 1,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(300).setAlpha(0).setScale(0.8)
    t.setStroke('#1a0505', 3)
    t.setShadow(0, 0, '#ff5546', 9, true, true)
    this.tweens.add({ targets: t, alpha: 1, scale: 1, duration: 400, ease: 'Back.Out' })

    this.powerText.setText(WEAPONS[wpn].name).setVisible(true)
    this.time.delayedCall(1000, () => {
      const epi = this.add.text(width / 2, 84, `Le cœur de ${this.stage.boss} est à vous...`, {
        fontSize: '9px', color: '#c7d2e8', fontFamily: 'monospace', fontStyle: 'italic',
      }).setOrigin(0.5).setScrollFactor(0).setDepth(300).setAlpha(0)
      this.tweens.add({ targets: epi, alpha: 1, duration: 500 })
    })
    this.time.delayedCall(1800, () => this.showStageClear())
    this.time.delayedCall(3800, () => {
      this.cameras.main.fadeOut(400, 0, 0, 0)
      this.time.delayedCall(450, () => this.scene.start('StageSelectScene'))
    })
  }

  private showAllTargetsDown() {
    const { width } = this.cameras.main
    const t = this.add.text(width / 2, 76, 'ALL TARGETS DOWN', {
      fontSize: '10px', color: '#ffb3b8', fontFamily: 'monospace', fontStyle: 'bold', letterSpacing: 2,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(300).setAlpha(0)
    t.setShadow(0, 0, '#ff2436', 6, true, true)
    this.tweens.add({ targets: t, alpha: 1, duration: 350, yoyo: true, hold: 900,
      onComplete: () => t.destroy() })
  }

  private spawnEnemies() {
    for (const d of this.ents.enemies) {
      let enemy: StageEnemy
      if (d.kind === 'flyer') enemy = new Flyer(this, d.x, d.y, this.player, undefined, this.diff)
      else if (d.kind === 'turret') enemy = new Turret(this, d.x, d.y, this.player, undefined, this.diff)
      else if (d.kind === 'charger') enemy = new Charger(this, d.x, d.y, this.player, this.diff)
      else if (d.kind === 'spitter') enemy = new Spitter(this, d.x, d.y, this.player, this.diff)
      else enemy = new Enemy(this, d.x, d.y, this.player, undefined, this.diff)
      this.enemies.add(enemy as unknown as Phaser.Physics.Arcade.Sprite)
      this.enemyCount++
    }
  }

  /** Un marcheur est tombé dans un trou en pourchassant : ajuster le compteur. */
  onEnemyFell(e: StageEnemy) {
    if (!e.active) return
    this.enemyCount--
    if (this.enemyCount <= 0) this.showAllTargetsDown()
  }

  /** Several holographic checkpoints: touching one moves the respawn point there. */
  private spawnCheckpoint() {
    this.checkpoints = []
    for (const cp of this.ents.checkpoints) {
      const { x, y } = cp
      const img = this.add.image(x, y, 'checkpoint').setDepth(10)
      this.physics.add.existing(img)
      const body = (img as Phaser.Physics.Arcade.Image).body as Phaser.Physics.Arcade.Body
      body.setAllowGravity(false)
      body.setSize(20, 32)
      this.checkpoints.push(img)

      this.physics.add.overlap(this.player, img, () => {
        // Ne réactive le checkpoint que si c'est un nouveau point (vers la droite).
        const prevX = this.registry.get('cpX') as number | undefined
        if (prevX === undefined || x > prevX) {
          this.registry.set('cpX', x)
          this.registry.set('cpY', y)
          img.setTint(0x7dfca2)
          sfx.checkpoint()
          void this.companionSay('checkpoint')
          this.spawnCollectBurst(x, y - 8, 0xff2436)
          const t = this.add.text(x, y - 34, 'CHECKPOINT', {
            fontSize: '8px', color: '#ffb3b8', fontFamily: 'monospace', fontStyle: 'bold', letterSpacing: 1,
          }).setOrigin(0.5).setDepth(120).setAlpha(0)
          this.tweens.add({ targets: t, alpha: 1, y: y - 40, duration: 500, ease: 'Cubic.Out',
            onComplete: () => this.tweens.add({ targets: t, alpha: 0, delay: 900, duration: 400, onComplete: () => t.destroy() }) })
        }
      }, undefined, this)
    }
    // Si on a déjà un checkpoint actif (retour de mort), teinter celui correspondant.
    const activeX = this.registry.get('cpX') as number | undefined
    if (activeX !== undefined) {
      const img = this.checkpoints.find(c => Math.abs(c.x - activeX) < 1)
      if (img) img.setTint(0x7dfca2)
    }
  }

  /** Aimed enemy projectile (boss volleys, turrets). */
  spawnEnemyBullet(x: number, y: number, tx: number, ty: number, speed = 350, tint = 0xff5546) {
    const b = this.enemyBullets.get(x, y, 'orb') as Phaser.Physics.Arcade.Image | null
    if (!b) return
    b.setTexture('orb')
    b.setActive(true).setVisible(true)
    b.body!.enable = true
    b.setTint(tint).setScale(1).setDepth(45)
    b.body!.setSize(6, 6)
    const ang = Math.atan2(ty - y, tx - x)
    const s = speed * this.diff.bulletMult
    b.setVelocity(Math.cos(ang) * s, Math.sin(ang) * s)
    const token = this.time.now + Math.random()
    b.setData('token', token)
    b.setData('kind', 'bullet') // réinit au cas où l'objet recyclé était une onde de choc
    this.time.delayedCall(3200, () => {
      if (b.active && b.getData('token') === token) b.disableBody(true, true)
    })
  }

  /** Ground shockwave from the boss slam. */
  spawnShockwave(x: number, y: number, dir: number) {
    const b = this.enemyBullets.get(x, y, 'orb') as Phaser.Physics.Arcade.Image | null
    if (!b) return
    b.setTexture('orb')
    b.setActive(true).setVisible(true)
    b.body!.enable = true
    b.setTint(0xffb347).setScale(1).setDepth(45)
    b.body!.setSize(6, 6)
    b.setVelocity(dir * 80, 0)
    const token = this.time.now + Math.random()
    b.setData('token', token)
    b.setData('kind', 'shockwave')
    this.time.delayedCall(1500, () => {
      if (b.active && b.getData('token') === token) b.disableBody(true, true)
    })
  }

  private handlePlayerHitByEnemyBullet(
    player: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    bulletObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ) {
    const p = player as Player
    const b = bulletObj as Phaser.Physics.Arcade.Image
    if (!b.active) return
    b.disableBody(true, true)
    this.spawnSparks(b.x, b.y)
    p.takeDamage(2, b.body!.velocity.x >= 0 ? -1 : 1)
  }

  /** Projectile ennemi bloqué par le sol : désactivé — sauf ondes de choc. */
  private handleEnemyBulletGround(
    bulletObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    _collider: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ) {
    const b = bulletObj as Phaser.Physics.Arcade.Image
    if (!b.active) return
    if (b.getData('kind') === 'shockwave') return
    this.spawnSparks(b.x, b.y)
    b.disableBody(true, true)
  }

  private spawnBoss() {
    const boss = new Boss(this, this.ents.bossX, 262, this.player, (hp, max) => this.drawBossBar(hp, max), undefined, this.diff)
    boss.setScale(1.35) // bien plus imposant que les sbires
    this.boss = boss
    this.bossActive = true
    // Colliders sur les DEUX couches : sans `ground`, le boss tombait à
    // travers le sol et se battait depuis sous le niveau (inhittable).
    this.physics.add.collider(this.boss, this.ground)
    this.physics.add.collider(this.boss, this.platforms)
    this.physics.add.overlap(
      this.bullets,
      this.boss,
      this.handleBulletHitBoss as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    )
    this.physics.add.overlap(
      this.player,
      this.boss,
      this.handlePlayerHitBoss as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    )
    this.drawBossBar()
  }

  private drawBossBar(hp?: number, max?: number) {
    const x = this.cameras.main.width - 140, y = 10, barW = 120, barH = 6
    this.bossBar.clear()
    if (!this.bossActive) {
      this.bossName.setVisible(false)
      return
    }
    this.bossName.setVisible(true)
    this.bossBar.fillStyle(0x0a0d16, 0.78).fillRoundedRect(x - 4, y - 4, barW + 8, barH + 8, 8)
    this.bossBar.lineStyle(1.5, 0x4a2432, 1).strokeRoundedRect(x - 4, y - 4, barW + 8, barH + 8, 8)
    this.bossBar.fillStyle(0x24101a, 1).fillRoundedRect(x, y, barW, barH, 5)
    const cur = hp ?? this.boss?.getHealth() ?? 0
    const m = max ?? this.boss?.getMaxHealth() ?? 1
    const frac = Math.max(0, cur / m)
    if (frac > 0) {
      const fw = Math.max(12, barW * frac)
      this.bossBar.fillGradientStyle(0xff8a7a, 0xff5566, 0xc81e3c, 0x8f1029, 1)
      this.bossBar.fillRoundedRect(x, y, fw, barH, 5)
      this.bossBar.fillStyle(0xffffff, 0.3).fillRoundedRect(x + 1, y + 1, Math.max(2, fw - 2), 2, 1)
    }
    this.bossBar.fillStyle(0x0a0d16, 0.55)
    for (let i = 1; i < 10; i++) this.bossBar.fillRect(x + (barW / 10) * i, y, 1.5, barH)
  }

  /** Mission briefing shown once at stage start — who, where, why. */
  private showBriefing() {
    const { width, height } = this.cameras.main
    const strip = this.add.rectangle(width / 2, height - 22, width, 34, 0x05060c, 0)
      .setScrollFactor(0).setDepth(240)
    const t1 = this.add.text(width / 2, height - 32, `MISSION 01 — ${this.stage.name}`, {
      fontSize: '8px', color: '#ffb3b8', fontFamily: 'monospace', fontStyle: 'bold', letterSpacing: 1,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(241).setAlpha(0)
    const t2 = this.add.text(width / 2, height - 18, `Libérez le secteur. Détruisez le ${this.stage.boss}.`, {
      fontSize: '8px', color: '#e2e8f0', fontFamily: 'monospace',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(241).setAlpha(0)
    this.tweens.add({ targets: strip, fillAlpha: 0.78, duration: 350 })
    this.tweens.add({ targets: [t1, t2], alpha: 1, duration: 350, delay: 200 })
    sfx.checkpoint()
    this.time.delayedCall(4200, () => {
      this.tweens.add({
        targets: [strip, t1, t2], alpha: 0, duration: 400,
        onComplete: () => { strip.destroy(); t1.destroy(); t2.destroy() },
      })
      if (!this.compSaid.has('start')) {
        this.compSaid.add('start')
        void this.companionSay('start')
      }
    })
  }

  /** ORION comments on progression — async AI with canned fallbacks. */
  private async companionSay(kind: string) {
    if (this.compBusy || this.compCooldown > 0) return
    this.compBusy = true
    const hpPct = Math.round((this.player.getHealth() / 10) * 100)
    const reply = await askAI(
      this.compData.persona + ' Réponds par UNE phrase courte (12 mots maximum), sans guillemets.',
      kind === 'start' ? 'Le stage commence. Un mot de soutien.'
      : kind === 'firstblood' ? 'Le premier ennemi vient d’être détruit. Commente.'
      : kind === 'clear' ? 'Tous les ennemis du secteur sont détruits. Commente.'
      : kind === 'lowhp' ? `Les points de vie de BLASTER-01 sont à ${hpPct}%. Alerte-le.`
      : kind === 'checkpoint' ? 'BLASTER-01 vient d’activer un checkpoint. Commente.'
      : `BLASTER-01 vient d’absorber le pouvoir du ${this.stage.boss}. Réagis.`,
      70, 7000)
    const pool = this.compData.lines[kind] ?? this.compData.lines.start
    const line = (reply && reply !== this.lastTaunt ? reply : pool[Math.floor(Math.random() * pool.length)])
    this.showCompanionSpeech(line)
    this.compCooldown = 8000
    this.time.delayedCall(4200, () => { this.compBusy = false })
  }

  /** Cyan comm bubble, bottom-left. */
  private showCompanionSpeech(text: string) {
    this.compBubble?.destroy()
    const { height } = this.cameras.main
    const cont = this.add.container(0, 0).setScrollFactor(0).setDepth(230)
    const box = this.add.rectangle(96, height - 26, 168, 30, 0x06101c, 0.88)
      .setStrokeStyle(1.5, this.compData.bubble, 0.85)
    const tag = this.add.text(20, height - 46, this.compData.name, {
      fontSize: '6px', color: '#ffb3b8', fontFamily: 'monospace', fontStyle: 'bold', letterSpacing: 1,
    })
    const msg = this.add.text(96, height - 24, '', {
      fontSize: '8px', color: '#d5ecf8', fontFamily: 'monospace',
      wordWrap: { width: 150 }, align: 'center',
    }).setOrigin(0.5, 0.5)
    cont.add([box, tag, msg])
    cont.setAlpha(0)
    this.tweens.add({ targets: cont, alpha: 1, duration: 180 })
    this.typeInto(msg, text)
    this.compBubble = cont
    this.time.delayedCall(3800, () => {
      if (!cont.active) return
      this.tweens.add({ targets: cont, alpha: 0, duration: 300, onComplete: () => {
        cont.destroy()
        if (this.compBubble === cont) this.compBubble = undefined
      } })
    })
  }

  /** Classic boss encounter intro: letterbox + WARNING flash. */
  private bossWarning() {
    this.bossIntroDone = true
    const { width, height } = this.cameras.main
    const top = this.add.rectangle(width / 2, 0, width, 0, 0x000000, 0.85).setScrollFactor(0).setDepth(240)
    const bottom = this.add.rectangle(width / 2, height, width, 0, 0x000000, 0.85).setScrollFactor(0).setDepth(240)
    this.tweens.add({ targets: top, height: 26, duration: 260 })
    this.tweens.add({ targets: bottom, height: 26, duration: 260 })
    sfx.telegraph()
    this.time.delayedCall(300, () => sfx.bossShot())

    const warn = this.add.text(width / 2, height / 2 - 14, 'WARNING', {
      fontSize: '20px', color: '#ff3524', fontFamily: 'monospace', fontStyle: 'bold', letterSpacing: 6,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(241).setAlpha(0)
    warn.setShadow(0, 0, '#ff3524', 10, true, true)
    const name = this.add.text(width / 2, height / 2 + 8, `${this.stage.boss} — GARDIEN CORROMPU`, {
      fontSize: '9px', color: '#ffb3a8', fontFamily: 'monospace', letterSpacing: 1,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(241).setAlpha(0)
    this.tweens.add({ targets: warn, alpha: 1, duration: 200, yoyo: true, repeat: 3 })
    this.tweens.add({ targets: name, alpha: 1, duration: 300, delay: 500 })

    this.time.delayedCall(2600, () => {
      this.tweens.add({
        targets: [top, bottom, warn, name], alpha: 0, duration: 350,
        onComplete: () => { top.destroy(); bottom.destroy(); warn.destroy(); name.destroy() },
      })
      void this.requestBossTaunt('intro')
    })
  }

  /** Called by the Boss when it enrages (HP <= 50%). */
  onBossEnraged() {
    void this.requestBossTaunt('enraged')
  }

  /** AI-generated taunt via /api/ai — async, never blocks; falls back to canned lines. */
  private async requestBossTaunt(kind: 'intro' | 'combat' | 'enraged') {
    if (this.bossTauntBusy || !this.bossActive) return
    this.bossTauntBusy = true
    const hpPct = Math.round(((this.boss?.getHealth() ?? 30) / 30) * 100)
    const playerHp = Math.round((this.player.getHealth() / 10) * 100)
    const userLine = kind === 'intro'
      ? 'Le combat commence. Une phrase d’ouverture menaçante.'
      : kind === 'enraged'
        ? `Ton intégrité tombe à ${hpPct}%. Une phrase de rage.`
        : `Combat en cours — ton intégrité ${hpPct}%, celle de BLASTER-01 ${playerHp}%. Une moquerie.`
    const text = (await askAI(
      `Tu es ${this.stage.boss}, une IA de guerre corrompue gardienne de ce secteur. Tu affrontes BLASTER-01, le dernier gardien loyal. Réponds par UNE seule phrase courte (12 mots maximum), froide et moqueuse. Sans guillemets, sans préfixe, sans didascalie.`,
      userLine, 70, 7000)) ?? ''
    if (text.length > 3 && text !== this.lastTaunt) {
      this.lastTaunt = text
      this.showBossSpeech(text)
    } else {
      const pool = this.fallbackTaunts[kind] ?? this.fallbackTaunts.combat
      const pick = pool[Math.floor(Math.random() * pool.length)]
      if (pick !== this.lastTaunt) {
        this.lastTaunt = pick
        this.showBossSpeech(pick)
      }
    }
    this.time.delayedCall(4300, () => { this.bossTauntBusy = false })
  }

  /** Machine-à-écrire : révèle le texte caractère par caractère. */
  private typeInto(msg: Phaser.GameObjects.Text, full: string) {
    msg.setText('')
    let i = 0
    this.time.addEvent({
      delay: 18,
      repeat: Math.max(0, full.length - 1),
      callback: () => {
        if (!msg.active) return
        i++
        msg.setText(full.slice(0, i))
      },
    })
  }

  /** Comm-line speech bubble under the boss bar. */
  private showBossSpeech(text: string) {
    this.bossSpeech?.destroy()
    const { width } = this.cameras.main
    const cont = this.add.container(0, 0).setScrollFactor(0).setDepth(230)
    const box = this.add.rectangle(width / 2, 66, width - 24, 30, 0x12060a, 0.88)
      .setStrokeStyle(1.5, 0xff5546, 0.9)
    const tag = this.add.text(width / 2 - (width - 24) / 2 + 8, 54, this.stage.boss, {
      fontSize: '6px', color: '#ff9d8a', fontFamily: 'monospace', fontStyle: 'bold', letterSpacing: 1,
    })
    const msg = this.add.text(width / 2, 69, '', {
      fontSize: '9px', color: '#ffd9d2', fontFamily: 'monospace',
      wordWrap: { width: width - 56 }, align: 'center',
    }).setOrigin(0.5, 0.5)
    cont.add([box, tag, msg])
    cont.setAlpha(0)
    this.tweens.add({ targets: cont, alpha: 1, duration: 180 })
    this.typeInto(msg, text)
    this.bossSpeech = cont
    this.time.delayedCall(3800, () => {
      if (!cont.active) return
      this.tweens.add({ targets: cont, alpha: 0, duration: 300, onComplete: () => {
        cont.destroy()
        if (this.bossSpeech === cont) this.bossSpeech = undefined
      } })
    })
  }

  private showStageClear() {
    const { width } = this.cameras.main
    const t = this.add.text(width / 2, 52, 'STAGE CLEAR', {
      fontSize: '18px', color: '#7dfca2', fontFamily: 'monospace', fontStyle: 'bold', letterSpacing: 3,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(300).setAlpha(0).setScale(0.7)
    t.setStroke('#05130b', 3)
    t.setShadow(0, 0, '#34d399', 10, true, true)
    this.tweens.add({ targets: t, alpha: 1, scale: 1, duration: 450, ease: 'Back.Out' })
  }

  /** Called by the Player when its HP reaches zero: life lost, respawn or game over. */
  onPlayerDeath(fxX = this.player.x, fxY = this.player.y) {
    this.respawning = true
    sfx.explode()
    this.spawnDeathEffect(fxX, fxY)
    const lives = ((this.registry.get('lives') as number) ?? 1) - 1
    this.registry.set('lives', lives)
    if (lives > 0) {
      // Respawn: restart the stage but keep the remaining lives (no `fresh` flag).
      this.time.delayedCall(650, () => this.scene.restart({ stage: this.stage.id }))
    } else {
      this.gameOver()
    }
  }

  /** Tombé dans un trou : perte d'une vie, effet au bord du vide, respawn checkpoint. */
  private playerFall() {
    this.respawning = true
    this.onPlayerDeath(this.player.x, Math.min(this.player.y, WORLD_H + 10))
  }

  private gameOver() {
    stopMusic()
    sfx.gameOver()
    const { width, height } = this.cameras.main
    const veil = this.add.rectangle(width / 2, height / 2, width, height, 0x05060c, 0)
      .setScrollFactor(0).setDepth(400)
    this.tweens.add({ targets: veil, fillAlpha: 0.85, duration: 600 })

    const t = this.add.text(width / 2, height / 2 - 10, 'GAME OVER', {
      fontSize: '22px', color: '#ff6b5e', fontFamily: 'monospace', fontStyle: 'bold', letterSpacing: 10,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(401).setAlpha(0).setScale(0.8)
    t.setStroke('#160404', 4)
    t.setShadow(0, 0, '#ff3524', 10, true, true)
    this.tweens.add({ targets: t, alpha: 1, scale: 1, duration: 500, ease: 'Back.Out' })

    const sub = this.add.text(width / 2, height / 2 + 13, 'RETURNING TO STAGE SELECT', {
      fontSize: '8px', color: '#8b93a8', fontFamily: 'monospace', letterSpacing: 1,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(401)
    this.tweens.add({ targets: sub, alpha: { from: 1, to: 0.25 }, duration: 600, yoyo: true, repeat: -1 })

    this.time.delayedCall(2800, () => {
      this.cameras.main.fadeOut(400, 0, 0, 0)
      this.time.delayedCall(450, () => this.scene.start('StageSelectScene'))
    })
  }

  private handleBulletHitBoss(
    a: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    b: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ) {
    // L'ordre des arguments d'overlap n'est pas garanti : identifier la balle.
    const bullet = a instanceof Bullet ? a : (b as Bullet)
    const bo = (a instanceof Bullet ? b : a) as Boss
    if (!bullet.active || !bo.active) return
    if (!bullet.canHit(bo)) return

    // Faiblesse d'arme : chaque gardien n'est blessé QUE par SON arme faible
    // (à deviner par le joueur). Avec la mauvaise arme, le boss « résiste ».
    const weak = BOSS_WEAKNESS[this.stage.id]
    if (weak !== undefined && bullet.weapon !== weak) {
      this.spawnBossBlocked(bullet.x, bullet.y)
      if (bullet.pierce) bullet.markHit(bo)
      else bullet.disableBody(true, true)
      return
    }

    this.spawnSparks(bullet.x, bullet.y)
    bo.takeDamage(bullet.damage)
    if (bullet.explode) this.explodeAt(bullet.x, bullet.y, 46, bullet.damage)
    if (bullet.freeze && bo.active) this.spawnSparks(bullet.x, bullet.y)
    if (bullet.pierce) bullet.markHit(bo)
    else bullet.disableBody(true, true)
  }

  /** Feedback visuel quand on frappe un gardien avec une arme qui n'est pas sa
   *  faiblesse : ondulation « bouclier » + mention « RÉSISTE! » (throttlée).
   *  Le joueur comprend ainsi qu'il faut une autre arme. */
  private spawnBossBlocked(x: number, y: number) {
    const shield = this.add.image(x, y, 'glow')
      .setTint(0x66ccff)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setScale(0.12)
      .setAlpha(0.9)
      .setDepth(120)
    this.tweens.add({
      targets: shield, scale: 0.75, alpha: 0, duration: 320, ease: 'Cubic.Out',
      onComplete: () => shield.destroy(),
    })
    if (this.time.now > this.lastBlockMsg) {
      this.lastBlockMsg = this.time.now + 900
      const t = this.add.text(x, y - 18, 'RÉSISTE!', {
        fontSize: '9px', color: '#8fd0ff', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(200)
      t.setStroke('#06202f', 3)
      this.tweens.add({ targets: t, y: y - 36, alpha: 0, duration: 750, onComplete: () => t.destroy() })
    }
  }

  private handlePlayerHitBoss(
    player: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    boss: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ) {
    const p = player as Player
    const bo = boss as Boss
    if (!bo.active) return
    p.takeDamage(1, bo.x < p.x ? 1 : -1)
  }

  /** Called by the Boss when its HP reaches zero: hides its HUD bar.
   *  Le corps du boss reste visible : c'est bossDeathSequence qui l'achève. */
  bossDefeated() {
    this.bossActive = false
    this.bossBar.clear()
    this.bossName.setVisible(false)
  }

  /**
   * Destruction du WAR MACHINE en trois temps (~2,2 s) :
   *  1. explosions en chaîne sur le corps, flashs blancs
   *  2. méga-blast final : flash écran, grosse secousse, ondes de choc, débris
   *  3. le noyau de pouvoir apparaît une fois l'onde passée
   */
  bossDeathSequence(
    target: { x: number; y: number; active: boolean; setTintFill(color?: number): unknown; clearTint(): unknown },
    onDone: () => void,
  ) {
    // Position figée à la mort : les explosions se placent sur le corps.
    const bx = target.x
    const by = target.y

    let n = 0
    const chain = this.time.addEvent({
      delay: 150,
      repeat: 9,
      callback: () => {
        n++
        const ox = Phaser.Math.Between(-22, 22)
        const oy = Phaser.Math.Between(-20, 16)
        this.spawnExplosion(bx + ox, by + oy, n % 3 === 0)
        if (n % 2 === 1) sfx.explode()
        // Clignotement blanc du châssis pendant que ça explose.
        if (n <= 6 && target.active) {
          target.setTintFill(0xffffff)
          this.time.delayedCall(70, () => { if (target.active) target.clearTint() })
        }
      },
    })

    // Méga-blast final.
    this.time.delayedCall(1600, () => {
      chain.remove()
      if (target.active) target.clearTint()
      sfx.bigExplode()
      this.cameras.main.flash(300, 255, 255, 255)
      this.cameras.main.shake(420, 0.009)
      this.spawnExplosion(bx, by, true)
      this.bossShockwaveRings(bx, by)
      this.spawnDebris(bx, by)
    })

    // Le noyau de pouvoir tombe une fois l'onde passée, puis le boss disparaît.
    this.time.delayedCall(2150, () => {
      this.spawnOrb(bx, by - 8, 'core')
      onDone()
    })
  }

  /** Triple anneau d'onde de choc (blanc -> ambre -> rouge). */
  private bossShockwaveRings(x: number, y: number) {
    const specs = [
      { color: 0xffffff, max: 92, dur: 480, delay: 0 },
      { color: 0xffd166, max: 66, dur: 520, delay: 90 },
      { color: 0xff5546, max: 44, dur: 560, delay: 180 },
    ]
    for (const s of specs) {
      const ring = this.add.circle(x, y, 6).setStrokeStyle(3, s.color, 1).setDepth(120)
      this.tweens.add({
        targets: ring, radius: s.max, alpha: 0, delay: s.delay, duration: s.dur,
        ease: 'Cubic.Out', onComplete: () => ring.destroy(),
      })
    }
  }

  /** Débris incandescents projetés vers le haut, retombant avec gravité. */
  private spawnDebris(x: number, y: number) {
    const p = this.add.particles(x, y, 'glow', {
      speed: { min: 60, max: 170 },
      angle: { min: 200, max: 340 },
      gravityY: 220,
      scale: { start: 0.14, end: 0 },
      lifespan: { min: 500, max: 900 },
      tint: [0xffd166, 0xff9a3c, 0xff5546, 0xffffff],
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    })
    p.explode(34)
    this.time.delayedCall(1000, () => p.destroy())
  }

  private handleBulletHitEnemy(
    a: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    b: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ) {
    // L'ordre des arguments d'overlap n'est pas garanti : identifier la balle.
    const bullet = a instanceof Bullet ? a : (b as Bullet)
    const e = (a instanceof Bullet ? b : a) as unknown as StageEnemy
    if (!bullet.active || !e.active) return
    if (!bullet.canHit(e)) return

    this.spawnSparks(bullet.x, bullet.y)
    e.takeDamage(bullet.damage)

    // Armes : VENOM explose (dégâts zone), CRYO gèle l'ennemi touché.
    if (bullet.explode) this.explodeAt(bullet.x, bullet.y, 46, bullet.damage, e)
    if (bullet.freeze && e.active) (e as unknown as { setData?: (k: string, v: unknown) => void }).setData?.('frozenUntil', this.time.now + 1300)

    if (!e.active) {
      this.enemyCount--
      if (Math.random() < 0.6) this.spawnOrb(e.x, e.y, 'hp')
      if (this.enemyCount <= 0) this.showAllTargetsDown()
    }

    if (bullet.pierce) bullet.markHit(e)
    else bullet.disableBody(true, true)
  }

  /** Explosion de zone (arme VENOM) : touche les ennemis voisins (hors la cible directe). */
  private explodeAt(x: number, y: number, r: number, dmg: number, exclude?: StageEnemy) {
    this.spawnExplosion(x, y, false)
    this.enemies.children.iterate((child) => {
      const en = child as unknown as StageEnemy
      if (!en.active || en === exclude) return true
      const p = child as unknown as { x: number; y: number }
      const dx = p.x - x
      const dy = p.y - y
      if (Math.hypot(dx, dy) <= r) {
        en.takeDamage(dmg)
        if (!en.active) {
          this.enemyCount--
          if (this.enemyCount <= 0) this.showAllTargetsDown()
        }
      }
      return true
    })
  }

  private handlePlayerHitEnemy(
    player: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    enemy: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ) {
    const p = player as Player
    const e = enemy as Enemy
    if (!e.active) return
    p.takeDamage(1, e.x < p.x ? 1 : -1)
  }
}
